import { create } from 'zustand';
import { isAccountBannedError, type AccountBannedError } from '../types/errors';
import { useAuthStore } from './auth.store';
import { isCurrentSession, issueRead, onSessionChange, type SessionRead } from './session-read';

/**
 * The "Account suspended" screen's state (components/AccountSuspendedScreen).
 *
 * suspend() is the ONE place an `account_banned` 403 is handled, whoever
 * received it: the API interceptor (any authenticated request), the password
 * and Apple logins (plain fetch), and the Google-callback ban notice. It clears
 * the auth state — the server has already revoked the token — and raises the
 * screen with the server's own message.
 *
 * A ban is addressed to the session its request was ISSUED under (c-057): the
 * caller takes `issueRead()` before sending and passes it here. An answer that
 * lands after that session ended (a sign-out, then a sign-in as someone else
 * without a reload) is for an account no longer on screen; it is ignored and
 * logged, so A's late 403 can neither sign B out nor raise A's notice over B.
 */
export interface AccountBanNotice {
  message: string;
  manageSubscriptionUrl: string | null;
}

export type BanNoticeOutcome = 'shown' | 'unavailable' | 'ended';

interface AccountBanState {
  notice: AccountBanNotice | null;
  /**
   * `sentUnder`: the ticket taken when the request that answered was issued.
   * Returns false when the ban was ignored because that session has ended.
   */
  suspend: (payload: AccountBannedError, sentUnder: SessionRead) => boolean;
  /**
   * Redeem a banned Google sign-in's notice for the suspended screen. The web
   * has none in hand — the server set it as an httpOnly cookie, sent with
   * credentials — and the app passes the one from its deep link, in the POST
   * body. Never a URL: GA and Sentry capture page URLs. Resolves 'shown' when
   * the screen was raised, 'unavailable' when the notice could not be redeemed
   * (the caller shows BANNED_FALLBACK), and 'ended' when the session it was
   * redeemed for changed meanwhile: nothing is shown for an account no longer
   * on screen.
   */
  redeemNotice: (notice?: string) => Promise<BanNoticeOutcome>;
  dismiss: () => void;
}

/** Shown when a ban notice cannot be redeemed (expired, offline). */
export const BANNED_FALLBACK = 'This account has been suspended. Contact support@empathease.app.';

/** Only ever Apple's own page; anything else in the field is ignored. */
function safeManageUrl(url: unknown): string | null {
  return typeof url === 'string' && url.startsWith('https://apps.apple.com/') ? url : null;
}

export const useAccountBanStore = create<AccountBanState>((set, get) => ({
  notice: null,

  suspend: (payload, sentUnder) => {
    if (!isCurrentSession(sentUnder)) {
      console.warn('[account-ban] ignored a late account_banned for a session that has ended');
      return false;
    }
    const auth = useAuthStore.getState();
    if (auth.isAuthenticated || auth.token) {
      // The whole of logout() on this device — the private socket closed, the
      // account's stores emptied — minus the API call the server would refuse.
      auth.clearSessionLocally();
    }
    set({
      notice: {
        message: payload.message,
        manageSubscriptionUrl: safeManageUrl(payload.manage_subscription_url),
      },
    });
    return true;
  },

  redeemNotice: async (notice) => {
    // A notice answers a Google sign-in, and a sign-in only starts signed out
    // (PublicRoute guards /login and /register, the only Google entry points).
    // So it is addressed to the signed-out screen, not to whoever is signed in
    // when it is redeemed: a deep link that lands with B on screen (B signed
    // in while A's browser trip was out, or a link someone else sent) must not
    // suspend B (c-057).
    const sentUnder = issueRead(null);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/ban-notice`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: notice ? JSON.stringify({ notice }) : undefined,
      });
      const payload: unknown = await response.json();
      if (!isCurrentSession(sentUnder)) return 'ended';
      if (isAccountBannedError(payload)) {
        get().suspend(payload, sentUnder);
        return 'shown';
      }
    } catch {
      /* fall through: the caller shows a generic message */
    }
    return isCurrentSession(sentUnder) ? 'unavailable' : 'ended';
  },

  dismiss: () => set({ notice: null }),
}));

// The notice must survive the sign-out the ban itself causes (A -> null), but
// not a NEW session: whoever signs in next is not the suspended account (#940).
onSessionChange((next) => {
  if (next) useAccountBanStore.getState().dismiss();
});
